import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { TelemetryService } from 'services/telemetry.service';
import { ConfigDbService } from 'services/config-db.service';
import { SettingsService } from 'services/settings.service';
import { CameraInitResult } from 'services/camera.service';
import { LoggingService } from 'services/logging.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { runScanFailureRecoveryAsync } from 'services/scan-error-policy';
import { showScanRetryAlertAsync } from 'services/scan-error-alert';
import { WakeLockService } from 'services/wake-lock.service';
import { ScanMode } from 'services/scan-config';
import { ScannerModelSelection } from 'services/scanner-model-selection';
import { FakeScanProgress } from 'services/scan-progress';
import { MultiModelFallbackManager, runMultiModelScanAsync } from 'services/multi-model-fallback';
import { openScannerImagePicker } from 'services/scanner-file-picker';
import {
  CameraStopOrdering,
  ScannerPageAnimationHooks,
  animateCapturedPhotoToReelAsync,
  animateCollageTransitionAsync,
} from 'services/scanner-capture-animations';
import { ZoomImagePopover } from 'components/zoom-image-popover/zoom-image-popover';
import { PopoverController } from 'providers/popover-controller';
import { OnlineStateService } from 'services/online-state.service';
import { AnimationController } from 'providers/animation-controller';
import { CsCameraViewComponent } from 'components/cs-camera-view/cs-camera-view';
import { CsScannerCameraStageComponent } from 'components/cs-scanner-camera-stage/cs-scanner-camera-stage';
import { CsScannerPhotoReelComponent } from 'components/cs-scanner-photo-reel/cs-scanner-photo-reel';
import {
  ScanEquipmentResults,
  ScanEquipmentError,
  ScanEquipmentQuotaRejection,
  EquipmentScanItem,
  EQUIPMENT_SCAN_CONFIDENCE_THRESHOLD,
  MAX_EQUIPMENT_SCAN_IMAGES,
  ScanSupportedEquipmentType,
} from '@cf-shared/scan-equipment-interface';
import { Equipment, EquipmentBar, EquipmentType } from '@cf-shared/data-model/equipment';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { formatMeasure } from 'util/formatting';
import { EquipmentScannerPageParams, EquipmentScannerPageResult } from './equipment-scanner-page-interface';
import { ScannerName } from 'services/camera-permission-help';
import {
  applyEquipmentScanResult,
  ApplyMode,
  convertWeightUnit,
  freshEquipmentOfType,
  isScannerEquipmentType,
  ScannerTypeKey,
  scannerItemKindForType,
  scannerKeyForType,
  scannerMicroPlateWeightsKg,
  scannerPrimaryWeightsKg,
  scannerTypeForKey,
  scannerTypeTakesMicroPlates,
} from './equipment-scanner-helpers';

interface ResultItem {
  source: EquipmentScanItem;
  /** Captured AI-assigned kind before any user-driven type-override remap. We need this because
   *  remapItemKinds() rewrites `source.kind` in place — without an immutable original we can't
   *  recover the AI's bar/collar/plate split when the user toggles back to the original type. */
  originalKind: EquipmentScanItem['kind'];
  checked: boolean;
  /** display string in the currently-selected unit (rebuilt on unit toggle) */
  display: string;
  /** confidence percentage for the badge, e.g. "92%" — a formatted value, not translatable text */
  confidencePct: TranslatedString;
  /** true when item.confidence < EQUIPMENT_SCAN_CONFIDENCE_THRESHOLD */
  lowConfidence: boolean;
}

@Component({
  selector: 'page-equipment-scanner',
  templateUrl: 'equipment-scanner-page.html',
  host: { 'class': 'equipment-scanner cs-scanner-page' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EquipmentScannerPage extends CsPage<EquipmentScannerPageParams> implements AfterViewInit {
  protected ScannerName = ScannerName;
  protected LL = LL;
  protected EquipmentType = EquipmentType;
  protected MAX_EQUIPMENT_SCAN_IMAGES = MAX_EQUIPMENT_SCAN_IMAGES;
  /** Test-mode-only flag controlling visibility of the "Mock" debug button that injects fake scan
   *  results without calling the AI. Drives the dev-mock-bar in the camera phase. */
  protected get showMockButton(): boolean { return this.settings.testMode; }

  get hasPhotos(): boolean { return this.capturedPhotos.length > 0; }
  get allPhotosTaken(): boolean { return this.capturedPhotos.length >= MAX_EQUIPMENT_SCAN_IMAGES; }
  get analyzing(): boolean { return this.phase === 'analyzing'; }

  @ViewChild('stage') stage: CsScannerCameraStageComponent;
  @ViewChild('reel') reel: CsScannerPhotoReelComponent;

  /** the wrapped camera view inside the shared camera stage */
  get cameraView(): CsCameraViewComponent | undefined {
    return this.stage?.cameraView;
  }

  cameraPermissionDismissed = false;
  // factories, not resolved strings — keeps the hints language-reactive
  readonly photoHints = [() => LL.equipmentScanner.photoHintCapture(), () => LL.equipmentScanner.photoHintLabel()];

  // page state
  phase: 'camera' | 'analyzing' | 'results' = 'camera';
  capturedPhotos: { dataUrl: string; base64: string }[] = [];
  retryMessage: TranslatedString = null;

  // shared fake-progress driver with the equipment scanner's cycling status lines
  private fakeProgress = new FakeScanProgress(
    [
      () => LL.equipmentScanner.analyzingReadingPlates(),
      () => LL.equipmentScanner.analyzingMatchingType(),
      () => LL.equipmentScanner.analyzingFinalising(),
    ],
    () => this.onProgressTick(),
  );

  get analyzeProgress(): number { return this.fakeProgress.progress; }
  get analyzeStatusText(): string { return this.fakeProgress.statusText; }

  // result state
  scanResult: ScanEquipmentResults = null;
  resultItems: ResultItem[] = [];
  suggestedName = '';
  nameAccepted = true;
  selectedUnit: 'kg' | 'lb' = 'kg';
  selectedApplyMode: ApplyMode = 'merge';
  currentValuesExpanded = false;

  /** User-selected option key from the type dropdown. Mirrors the equipment-edit template list:
   *  'barbell' | 'plates' | 'cable-machine' | 'dumbbells' | 'kettlebells'. Two of these (dumbbells +
   *  kettlebells) collapse onto the same EquipmentType.Fixed; the distinction is purely cosmetic
   *  (drives the seeded suggestedName) but keeps the dropdown 1:1 with what the user sees on the
   *  equipment-edit page. Use `selectedEquipmentType` for internal logic. */
  selectedKey: ScannerTypeKey | undefined;

  /** Internal EquipmentType derived from `selectedKey`. Two keys (`dumbbells`, `kettlebells`) collapse
   *  onto EquipmentType.Fixed; the rest map 1:1. Returns undefined when no key is selected (auto-detect
   *  came back 'unknown' and the user hasn't chosen yet). */
  get selectedEquipmentType(): ScanSupportedEquipmentType | undefined {
    return this.selectedKey ? scannerTypeForKey(this.selectedKey) : undefined;
  }

  // model config — shared with the other scanners. Non-private: the template
  // reads it to render the shared 'any'-mode model picker (#3783)
  readonly modelSelection = new ScannerModelSelection();
  private multiModelFallback = new MultiModelFallbackManager();

  // what the shared capture animations need from this page. A getter, not a
  // field: the injected animationController is not assigned yet while field
  // initialisers run.
  private get animationHooks(): ScannerPageAnimationHooks {
    return {
      animationController: this.animationController,
      cameraElement: () => this.cameraView?.getCameraElement() ?? null,
      reelSlotElements: () => this.reel?.slotElements ?? [],
      collagePhotoElements: () => this.stage?.collagePhotoElements ?? [],
      capturedPhotos: () => this.capturedPhotos,
      isViewDestroyed: () => this.viewDestroyed,
      markForCheck: () => this.cd.markForCheck(),
    };
  }

  // draft (from nav params) — non-private so the template can read draft.type.
  // undefined when the page is launched in auto-detect mode (no template chosen yet).
  draft: Equipment | undefined;

  constructor(
    private settings: SettingsService,
    private logging: LoggingService,
    private navCtrl: NavController,
    private cloudFunctions: CloudFunctionsService,
    private telemetry: TelemetryService,
    private configDb: ConfigDbService,
    private alertCtrl: AlertController,
    private cd: ChangeDetectorRef,
    private wakeLock: WakeLockService,
    private animationController: AnimationController,
    private popoverCtrl: PopoverController,
    private onlineState: OnlineStateService,
  ) {
    super();
  }

  async ngAfterViewInit() {
    this.draft = this.navParams.data.local?.equipmentDraft;
    // when no draft is passed, the page runs in auto-detect mode — the AI infers the type from
    // the photo and we synthesise a fresh equipment on apply
    await this.modelSelection.initAsync(this.configDb, this.settings);
    if (this.viewDestroyed) return;
    this.cd.markForCheck();
  }

  // the shared cs-scanner-model-picker emits the chosen key; the shared
  // selection persists it to the one setting every scanner reads (#3783)
  onModelSelected(key: string) {
    this.modelSelection.onModelSelected(key, this.settings);
    this.cd.markForCheck();
  }

  // ── Camera + capture ───────────────────────────────────────────────

  onCameraStateChange() {
    this.cameraPermissionDismissed = this.cameraView?.cameraService.cameraPermissionDismissed ?? false;
    this.cd.markForCheck();
  }

  onCameraError(result: CameraInitResult) {
    if (result === CameraInitResult.NoCameraWorked) {
      if (this.cameraView?.cameraService.permissionStatus?.state === 'granted') {
        void this.showErrorAlertAsync(LL.equipmentScanner.errorCamera());
      }
    }
  }

  // haptic + shutter sound are handled by cs-scanner-shutter-bar
  async snapPhoto() {
    if (this.allPhotosTaken) return;
    const photo = await this.cameraView.capturePhoto();
    if (!photo) return;
    const slotIndex = this.capturedPhotos.length;
    // replace (not mutate) the array — the OnPush reel/stage components
    // only re-render when the photos input gets a new reference
    this.capturedPhotos = [...this.capturedPhotos, photo];
    this.telemetry.trackEvent('equipment_scan_photo_taken', { source: 'camera' });
    this.cd.markForCheck();
    await animateCapturedPhotoToReelAsync(this.animationHooks, slotIndex, photo.dataUrl);
  }

  // upload from the photo library via the shared scanner file picker
  openFilePicker() {
    if (this.allPhotosTaken) return;
    openScannerImagePicker({
      safeTimeout: (callback, ms) => this.safeTimeout(callback, ms),
      // let the user pick several photos at once, capped at the remaining slots
      maxFiles: MAX_EQUIPMENT_SCAN_IMAGES - this.capturedPhotos.length,
      onPicked: async photo => {
        if (this.viewDestroyed || this.allPhotosTaken) return;
        const slotIndex = this.capturedPhotos.length;
        // replace (not mutate) — see snapPhoto
        this.capturedPhotos = [...this.capturedPhotos, photo];
        this.telemetry.trackEvent('equipment_scan_photo_taken', { source: 'upload' });
        this.cd.markForCheck();
        await animateCapturedPhotoToReelAsync(this.animationHooks, slotIndex, photo.dataUrl);
      },
    });
  }

  /** Tap an empty reel slot to take another photo when camera is inactive. */
  addPhoto() {
    if (this.allPhotosTaken) return;
    void this.cameraView?.reinitCamera();
  }

  /** Remove a photo (red X on its reel slot / tap on a collage photo)
   *  and reactivate the camera. */
  removePhotoAt(index: number) {
    this.telemetry.trackEvent('equipment_scan_photo_removed', { photoIndex: index });
    // replace (not mutate) — see snapPhoto
    this.capturedPhotos = this.capturedPhotos.filter((_, i) => i !== index);
    this.cd.markForCheck();
    void this.cameraView?.reinitCamera();
  }

  // from the results screen: discard the scan results and start over with
  // the camera. The camera stage remounts when the phase flips back, and
  // the fresh cs-camera-view starts the camera itself (same as page entry).
  retakeFromResults() {
    this.scanResult = null;
    this.resultItems = [];
    this.capturedPhotos = [];
    this.phase = 'camera';
    this.cd.markForCheck();
  }

  // discard all captured photos and resume shooting (the collage photos
  // themselves are not tappable — mirrors the meal/label Retake)
  retakeAllPhotos() {
    this.capturedPhotos = [];
    this.cd.markForCheck();
    // the camera usually stayed active on this page; restart it only if not
    if (!this.cameraView?.isActive) {
      void this.cameraView?.reinitCamera();
    }
  }

  // tap a reel thumbnail to inspect the photo full-screen. On native the
  // camera preview composites outside the DOM (iOS cuts a mask hole in
  // the WebView, Android draws above it), so a DOM popover over the
  // camera rect is invisible — pause the camera while inspecting.
  async inspectPhotoAt(index: number) {
    const photo = this.capturedPhotos[index];
    if (!photo) return;

    const pauseCamera = !!this.cameraView?.isActive && this.cameraView.isNative;
    if (pauseCamera) {
      await this.cameraView.stopCamera();
      if (this.viewDestroyed) return;
      this.cd.markForCheck();
    }

    // resolves when the popover is dismissed
    await this.popoverCtrl.present(ZoomImagePopover, { imageSrc: photo.dataUrl, fullscreen: true }, {
      targetFromElement: this.reel?.slotElements[index],
      targetToElement: document.body,
      animate: true,
    });
    if (this.viewDestroyed) return;

    if (pauseCamera) {
      void this.cameraView?.reinitCamera();
    }
  }

  get canSubmit(): boolean {
    return this.capturedPhotos.length > 0 && this.phase === 'camera';
  }

  // ── Analyze ────────────────────────────────────────────────────────

  // submit captured photos through the (possibly multi-model) equipment
  // scanner pipeline and surface results / errors. Holds the screen awake for
  // the duration of the inference round-trip (release in finally).
  async analyzeAsync() {
    if (!this.canSubmit) return;
    // stop the camera so the analyzing overlay (inside the stage's collage
    // container) actually renders during analysis; the reel thumbnails fly
    // to their collage positions on the way (owner decision #17)
    if (this.cameraView?.isActive) {
      await animateCollageTransitionAsync(this.animationHooks, {
        isCameraActive: !!this.cameraView?.isActive,
        stopCameraAsync: async () => { await this.cameraView?.stopCamera(); },
        cameraStopOrdering: CameraStopOrdering.BeforeCollage,
      });
      if (this.viewDestroyed) return;
    }
    this.phase = 'analyzing';
    this.retryMessage = null;
    this.fakeProgress.start();
    this.cd.markForCheck();

    try {
      // hold screen awake for the (possibly multi-model) inference round-trip;
      // released in finally regardless of success / error / viewDestroyed
      // early return. Inside the try so finally is the single release site.
      void this.wakeLock.acquireAsync('scanner:equipment');
      const result = await this.runScanAsync();
      this.fakeProgress.stop();
      if (this.viewDestroyed) return;

      // hard quota rejection
      if (result?.quota?.rejection && result.quota.rejection !== ScanEquipmentQuotaRejection.QualityWarning) {
        await this.handleQuotaRejection(result);
        this.phase = 'camera';
        this.cd.markForCheck();
        return;
      }

      // provider / network errors: the shared policy keeps the photos and offers
      // a Try Again for transient failures (offline / provider error / timeout /
      // incomplete), and resets to a fresh camera for the non-retryable ones. The
      // message stays equipment-specific: network copy offline, incomplete copy for
      // truncation/timeout, and translateScanError (unsupported / bad-image /
      // generic) otherwise.
      if (result?.error) {
        // read the connectivity belief ONCE. Calling isLikelyOnline() twice can return two
        // different answers if the state flips between the calls, which would log the failure as
        // 'network' while showing the user the online copy (or the reverse).
        const isOnline = this.onlineState.isLikelyOnline();
        // preserve the existing telemetry distinction (offline is logged as 'network')
        this.telemetry.trackEvent('equipment_scan_error', { error: isOnline ? result.error : 'network' });
        await this.handleScanFailureAsync(result.error, this.scanErrorMessage(result.error, isOnline));
        return;
      }

      // no usable items — a content error: retrying the same photos won't help, so
      // show the specific reason and reset to a fresh camera for a retake.
      if (result?.noEquipmentReason || !result?.items || result.items.length === 0) {
        this.telemetry.trackEvent('equipment_scan_no_equipment', { reason: result?.noEquipmentReason });
        let message: TranslatedString;
        switch (result?.noEquipmentReason) {
          case 'blurry':          message = LL.equipmentScanner.noEquipmentBlurry(); break;
          case 'mixed_equipment': message = LL.equipmentScanner.noEquipmentMixed(); break;
          default:                message = LL.equipmentScanner.noEquipmentDetected(); break;
        }
        await this.showScanErrorAsync(message, { resetCamera: true });
        return;
      }

      // success
      this.telemetry.trackEvent('equipment_scan_success', {
        type: this.draft?.type,
        itemCount: result.items.length,
        modelKey: result.modelKey,
        detectedType: result.detectedType,
      });
      this.scanResult = result;
      this.suggestedName = result.suggestedName ?? '';
      // when editing equipment that already has a name, never overwrite by default
      this.nameAccepted = !this.draft?.name;
      this.selectedUnit = result.detectedUnit ?? 'kg';
      // initial selectedKey comes from the AI's detectedType; if a draft exists we lock to its type.
      // If the AI returned 'unknown' we leave selectedKey undefined so the dropdown shows the
      // placeholder and forces the user to pick a real type.
      this.selectedKey = this.draft
        ? scannerKeyForType(this.draft.type as EquipmentType)
        : scannerKeyForType(result.detectedType);
      this.buildResultItems();
      this.phase = 'results';
      this.cd.markForCheck();
    } catch (err) {
      this.fakeProgress.stop();
      if (this.viewDestroyed) return;
      this.telemetry.trackEvent('equipment_scan_error', { error: 'network' });
      this.logging.trackAbnormalException(err);
      // a thrown request error is transient (network drop): keep the photos + offer
      // Try Again with the network copy. ProviderError is the stand-in error code.
      await this.handleScanFailureAsync(ScanEquipmentError.ProviderError, LL.equipmentScanner.errorNetwork());
    } finally {
      void this.wakeLock.releaseAsync('scanner:equipment');
    }
  }

  /** Single-call or multi-model fallback (shared blacklist + shuffle + retry
   *  loop; see multi-model-fallback.ts). */
  private async runScanAsync(): Promise<ScanEquipmentResults> {
    const images = this.capturedPhotos.map(p => p.base64);
    // pass equipmentType only if we have a draft; otherwise the server auto-detects
    const equipmentType = this.draft ? (this.draft.type as ScanSupportedEquipmentType) : undefined;

    if (this.modelSelection.scanMode === ScanMode.Multi) {
      return runMultiModelScanAsync<ScanEquipmentResults>({
        models: this.modelSelection.multiModels,
        manager: this.multiModelFallback,
        isOnline: () => this.onlineState.isLikelyOnlineOrRecheckAsync(),
        scanAsync: modelKey =>
          // the server does not read `locale` for this scanner yet — the branding it reads off
          // the machine is copied as printed. Sent so a future framing hook can use it (#4206)
          this.cloudFunctions.scanEquipmentAsync({ imagesBase64: images, equipmentType, model: modelKey, locale: BOOT_LOCALE }),
        isProviderSideError: result =>
          result.error === ScanEquipmentError.ProviderError || result.error === ScanEquipmentError.ProviderTimeout || result.error === ScanEquipmentError.IncompleteResponse,
        makeNetworkErrorResult: () => ({ error: ScanEquipmentError.ProviderError }),
        isAborted: () => this.viewDestroyed,
        onRetry: () => this.showRetryFeedback(),
        onNetworkError: (modelKey, err) =>
          console.log(`Equipment scan: ${modelKey} network error:`, (err as Error)?.message),
      });
    }

    // single-model or 'all' mode passes the configured model directly
    return this.cloudFunctions.scanEquipmentAsync({ imagesBase64: images, equipmentType, model: this.modelSelection.fixedModel, locale: BOOT_LOCALE });
  }

  private showRetryFeedback() {
    this.retryMessage = LL.equipmentScanner.retryingDifferentServer();
    // restart the bar from zero for the next model attempt
    this.fakeProgress.start();
    this.cd.markForCheck();
  }

  private translateScanError(err: string): TranslatedString {
    if (err === ScanEquipmentError.UnsupportedEquipmentType) return LL.equipmentScanner.errorUnsupportedType();
    if (err === ScanEquipmentError.TooManyImages || err === ScanEquipmentError.ImageTooLarge) return LL.equipmentScanner.errorBadImage();
    return LL.equipmentScanner.errorGeneric();
  }

  private async handleQuotaRejection(result: ScanEquipmentResults) {
    let message: TranslatedString;
    switch (result.quota.rejection) {
      case ScanEquipmentQuotaRejection.AccountLocked:
        message = LL.equipmentScanner.quotaAccountLocked();
        break;
      case ScanEquipmentQuotaRejection.RateLimited:
        message = LL.equipmentScanner.quotaRateLimited();
        break;
      case ScanEquipmentQuotaRejection.DailyCooldown:
        message = LL.equipmentScanner.quotaDailyCooldown();
        break;
      case ScanEquipmentQuotaRejection.GlobalCap:
        message = LL.common.messages.scanAppCapacityReached();
        break;
      default:
        return;
    }
    await this.alertCtrl.showAlertAsync({
      title: LL.equipmentScanner.quotaTitle(),
      message,
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
  }

  // ── Result item construction ───────────────────────────────────────

  private buildResultItems() {
    if (!this.scanResult?.items) {
      this.resultItems = [];
      return;
    }
    // sort ascending by weight so detected and resulting cards match. bar/collar are kept first
    // (per their kind) so the Bar grouping renders bar → collar → plates in a stable order;
    // within each kind we sort by weight.
    const KIND_ORDER: Record<EquipmentScanItem['kind'], number> = { bar: 0, collar: 1, stack: 2, plate: 3, fixed: 4 };
    const sorted = [...this.scanResult.items].sort((a, b) => {
      const k = KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
      return k !== 0 ? k : a.weight - b.weight;
    });
    this.resultItems = sorted.map(item => this.makeResultItem(item));
  }

  private makeResultItem(item: EquipmentScanItem): ResultItem {
    return {
      source: item,
      // remember the AI's original kind so type-override remaps stay reversible
      originalKind: item.kind,
      checked: item.confidence >= EQUIPMENT_SCAN_CONFIDENCE_THRESHOLD,
      display: this.formatItemWeight(item),
      confidencePct: untranslatedString(`${Math.round(item.confidence * 100)}%`),
      lowConfidence: item.confidence < EQUIPMENT_SCAN_CONFIDENCE_THRESHOLD,
    };
  }

  private formatItemWeight(item: EquipmentScanItem): string {
    // The unit toggle does NOT convert the number — it relabels. If the AI mis-detected the unit,
    // the user picks the correct one and the displayed (and applied) value is `item.weight <selectedUnit>`.
    // We pretend the AI's number is in `selectedUnit`, convert that to kg only to satisfy formatMeasure's
    // canonical-kg input contract, then formatMeasure converts back to selectedUnit for display.
    const valueKg = convertWeightUnit(item.weight, this.selectedUnit, 'kg');
    return formatMeasure(valueKg, 'addedweight_kg', this.settings.locale, this.selectedUnit);
  }

  onUnitToggle() {
    // rebuild display strings for both detected and current lists
    for (const item of this.resultItems) {
      item.display = this.formatItemWeight(item.source);
    }
    this.cd.markForCheck();
  }

  /** Per-type label for the resulting-values drawer header (collapsed and expanded variants).
   *
   *  Switch-on-literal with no default arm, matching `getEquipmentTemplateLabel` in
   *  pages/equipment/equipment-templates.ts: the label strings stay written out one per type so
   *  typesafe-i18n's typed accessors survive, and `noImplicitReturns` turns a type added without a
   *  label here into a compile error rather than a blank drawer heading. */
  currentValuesLabel(expanded: boolean): string {
    const previewType = this.previewDraft?.type;
    if (!isScannerEquipmentType(previewType)) return '';
    const count = this.resultPreviewCount;
    const cv = LL.equipmentScanner.currentValues;
    switch (previewType) {
      case EquipmentType.Stack:  return expanded ? cv.headerStack({ count })  : cv.showStack({ count });
      case EquipmentType.Plates: return expanded ? cv.headerPlates({ count }) : cv.showPlates({ count });
      case EquipmentType.Bar:    return expanded ? cv.headerBar({ count })    : cv.showBar({ count });
      case EquipmentType.Fixed:  return expanded ? cv.headerFixed({ count })  : cv.showFixed({ count });
    }
  }

  toggleCurrentValues() {
    this.currentValuesExpanded = !this.currentValuesExpanded;
    this.cd.markForCheck();
  }

  // ── Apply / Abandon ────────────────────────────────────────────────

  get checkedCount(): number {
    return this.resultItems.filter(i => i.checked).length;
  }

  /** The post-apply equipment preview — what the draft will look like after the user taps Apply.
   *  Drives both the header counts and the per-row preview tables in the "Resulting …" card.
   *  Returns null when we can't synthesise a base (no draft + detectedType is unknown). */
  get previewDraft(): Equipment | null {
    if (!this.scanResult) return null;
    const checked = this.resultItems.filter(i => i.checked).map(i => i.source);
    let baseDraft = this.draft;
    if (!baseDraft) {
      const t = this.selectedEquipmentType;
      if (!t) return null;
      baseDraft = freshEquipmentOfType('', t);
    }
    // when there's no draft, mode doesn't matter (empty base), but use 'replace' for clarity.
    const mode = this.draft ? this.selectedApplyMode : 'replace';
    // use selectedUnit (not detectedUnit) so the unit segment relabels rather than converts —
    // matches the display logic in formatItemWeight()
    return applyEquipmentScanResult(baseDraft, checked, mode, this.selectedUnit);
  }

  /** Number of items in the resulting PRIMARY list after apply (stack increments / dumbbell weights /
   *  plates) — drives the type-specific drawer header (e.g. "Resulting stack (N increments)"), which
   *  names that primary list and so must exclude micro plates. */
  get resultPreviewCount(): number {
    const p = this.previewDraft;
    return p ? scannerPrimaryWeightsKg(p).length : 0;
  }

  /** Total number of resulting items including micro plates — for the generic "After apply: N items"
   *  summary and the drawer-visibility guard, so they match the detected-side total (which counts every
   *  kind). For Stack/Fixed this adds the micro `plates[]`; for other types it equals resultPreviewCount. (#3523) */
  get resultPreviewTotalCount(): number {
    return this.resultPreviewCount + this.currentMicroPlateRows.length;
  }

  get isApplyDisabled(): boolean {
    if (this.checkedCount === 0) return true;
    // lock Apply on any unrecognized or mismatched detected type — applying through a
    // type mismatch would silently drop every item (helper filters by item.kind),
    // wiping the existing list in Replace mode and being a no-op in Merge/Append
    return this.showTypeMismatchBanner;
  }

  get showTypeMismatchBanner(): boolean {
    // selectedKey unset → user hasn't picked a type yet (AI returned 'unknown' or undefined in
    // auto-detect mode). Show the "unknown" banner so Apply is blocked until the user picks.
    if (!this.selectedKey) return true;
    // in edit mode, the dropdown is locked to draft.type — compare the AI's detected type to the
    // draft type so the banner still fires when the AI mis-classifies. Without this check, items
    // with non-matching kinds would silently be dropped by applyEquipmentScanResult (and Replace
    // mode would wipe the existing list).
    if (this.draft) {
      const detected = this.scanResult?.detectedType;
      if (!detected || detected === 'unknown') return detected === 'unknown';
      return detected !== this.draft.type;
    }
    // auto-detect mode: only block when the user-selected type doesn't match itself (currently
    // can only happen if selectedKey resolves to undefined, which is guarded above).
    return false;
  }

  get typeMismatchMessage(): TranslatedString {
    const detected = this.scanResult?.detectedType;
    if (detected === 'unknown') return LL.equipmentScanner.unknownTypeBanner();
    return LL.equipmentScanner.typeMismatchBanner({ detected: String(detected), current: String(this.draft?.type ?? '') });
  }

  confirm() {
    if (this.isApplyDisabled) return;
    const checked = this.resultItems.filter(i => i.checked).map(i => i.source);
    const acceptedName = this.nameAccepted && this.suggestedName ? this.suggestedName : undefined;
    // auto-detect mode: synthesise a fresh draft of the user-selected type so the helper has a base
    // to apply onto. We use selectedEquipmentType (which reflects user dropdown overrides) rather
    // than scanResult.detectedType — otherwise overriding the AI's type and applying would build
    // the draft with the AI's original type, mismatching the re-keyed items. Banner gate above
    // (isApplyDisabled) prevents reaching here when selectedEquipmentType is undefined.
    let baseDraft = this.draft;
    if (!baseDraft) {
      const selected = this.selectedEquipmentType;
      if (!selected) return; // shouldn't happen — banner gate would have blocked Apply
      baseDraft = freshEquipmentOfType('', selected);
    }
    // use selectedUnit (not the AI's detectedUnit) so the toggle relabels — if the AI mis-detected
    // the unit the user picks the correct one and the raw detected number is treated as that unit
    const updatedDraft = applyEquipmentScanResult(baseDraft, checked, this.selectedApplyMode, this.selectedUnit, acceptedName);
    this.telemetry.trackEvent('equipment_scan_applied', {
      mode: this.selectedApplyMode,
      checkedCount: checked.length,
      itemCount: this.resultItems.length,
      autoDetect: !this.draft,
    });
    void this.navCtrl.popAsync({ updatedDraft } as EquipmentScannerPageResult);
  }

  /** Dev-only: skip the AI call and jump straight to results with a fixture so the review screen
   *  can be iterated on without burning provider tokens. Generates a payload tailored to the current
   *  draft type (or a Stack fixture in auto-detect mode). */
  async mockAnalyze() {
    if (!this.showMockButton) return;
    if (this.cameraView?.isActive) {
      await this.cameraView.stopCamera();
      if (this.viewDestroyed) return;
    }
    // a draft of a type the scanner cannot produce (Manual / Unknown), or no draft at all, falls
    // back to the Stack fixture — the same choice the auto-detect entry point makes
    const requestedType = this.draft?.type;
    const detectedType: ScanSupportedEquipmentType = isScannerEquipmentType(requestedType) ? requestedType : EquipmentType.Stack;
    const { suggestedName, items } = mockScanFixture(detectedType);

    const fakeResult: ScanEquipmentResults = {
      detectedType,
      suggestedName,
      detectedUnit: 'kg',
      confidence: 0.9,
      items,
      modelKey: 'mock',
      scanId: 'mock-' + Date.now(),
    };

    this.scanResult = fakeResult;
    this.suggestedName = suggestedName;
    this.nameAccepted = !this.draft?.name;
    this.selectedUnit = 'kg';
    this.selectedKey = this.draft ? scannerKeyForType(this.draft.type as EquipmentType) : scannerKeyForType(detectedType);
    this.buildResultItems();
    this.phase = 'results';
    this.cd.markForCheck();
  }

  // ── Helpers for current-values drawer ──────────────────────────────

  /** Per-type rows for the post-apply preview drawer. All read from `previewDraft`, so they reflect
   *  the result of the chosen Apply mode against the checked detected items, not the pre-scan state.
   *
   *  Each getter stays separate because the template asks for them by name and renders them
   *  differently — Bar shows three labelled groups, the other types one flat table — but the rows
   *  themselves all come from the one per-type table in equipment-scanner-helpers.ts. */
  get currentBarRow(): string | null {
    return this.formatOptionalWeightKg(this.previewBar()?.bar_kg);
  }
  get currentCollarRow(): string | null {
    return this.formatOptionalWeightKg(this.previewBar()?.collar_kg);
  }
  get currentPlateRows(): string[] {
    // for Bar and Plates the equipment's `plates[]` IS the primary list
    return this.primaryRowsWhenPreviewIs(EquipmentType.Bar, EquipmentType.Plates);
  }
  get currentStackRows(): string[] {
    return this.primaryRowsWhenPreviewIs(EquipmentType.Stack);
  }
  get currentFixedRows(): string[] {
    return this.primaryRowsWhenPreviewIs(EquipmentType.Fixed);
  }
  /** Resulting microloading add-on plates for the Stack/Fixed preview drawer (the `plates[]`
   *  micro-plate field). Empty for other types. (#3523) */
  get currentMicroPlateRows(): string[] {
    const p = this.previewDraft;
    return p ? this.formatWeightsKg(scannerMicroPlateWeightsKg(p)) : [];
  }

  /** The preview draft as a bar, or undefined when the preview is some other type. Only Bar carries
   *  the single bar and collar weights, which is why they have no place in the per-type table. */
  private previewBar(): EquipmentBar | undefined {
    const p = this.previewDraft;
    return p?.type === EquipmentType.Bar ? (p as EquipmentBar) : undefined;
  }

  /** The preview's primary list, but only when it is one of the given types — the template renders
   *  each type's primary rows in its own branch, so a Stack preview must not fill the plate rows. */
  private primaryRowsWhenPreviewIs(...types: ScanSupportedEquipmentType[]): string[] {
    const p = this.previewDraft;
    if (!p || !types.includes(p.type as ScanSupportedEquipmentType)) return [];
    return this.formatWeightsKg(scannerPrimaryWeightsKg(p));
  }

  private formatWeightsKg(valuesKg: number[]): string[] {
    return valuesKg.map(value => formatMeasure(value, 'addedweight_kg', this.settings.locale, this.selectedUnit));
  }

  private formatOptionalWeightKg(valueKg: number | undefined): string | null {
    if (valueKg == null) return null;
    return formatMeasure(valueKg, 'addedweight_kg', this.settings.locale, this.selectedUnit);
  }

  /** True when the type select should be locked (existing equipment is being edited; we know the type). */
  get isTypeLocked(): boolean {
    return !!this.draft;
  }

  /** When the user picks a different type from the dropdown — re-key all detected items so the helper
   *  applies them to the right field (stack increments, plate weights, fixed dumbbell weights, …).
   *  Bar is special because it has multiple item kinds; switching to/from Bar collapses everything to plates. */
  onTypeOverride(newKey: ScannerTypeKey) {
    this.selectedKey = newKey;
    this.remapItemKinds();
    this.cd.markForCheck();
  }

  /** Re-key each ResultItem's `source.kind` from the user's selected type, deriving from the
   *  captured `originalKind` (the AI's verdict) rather than from the current `source.kind`. This
   *  keeps toggles reversible — switching Plates → Bar → Plates restores the AI's original
   *  bar/collar/plate split instead of collapsing every item to "plate". */
  private remapItemKinds() {
    const t = this.selectedEquipmentType;
    if (!t) return;
    for (const item of this.resultItems) {
      item.source.kind = scannerItemKindForType(t, item.originalKind);
    }
  }

  /** What kind of detected list to render. Driven by the user-selected type (which seeds from the
   *  AI's detectedType) when in auto-detect mode, or by the draft type when editing existing equipment. */
  get displayType(): EquipmentType | undefined {
    return this.selectedEquipmentType ?? this.draft?.type;
  }

  /** Detected items grouped by kind, for the Bar-type rendering. Returns null when display type isn't Bar. */
  get detectedBarRows(): { bar?: ResultItem; collar?: ResultItem; plates: ResultItem[] } | null {
    if (this.displayType !== EquipmentType.Bar) return null;
    return {
      bar: this.resultItems.find(i => i.source.kind === 'bar'),
      collar: this.resultItems.find(i => i.source.kind === 'collar'),
      plates: this.resultItems.filter(i => i.source.kind === 'plate'),
    };
  }

  /** True when the detected list being shown is a Stack or Fixed type — the two types that carry an
   *  add-on `plates[]` micro-plate field and so split their detected items into a primary group plus a
   *  micro-plate group. (#3523) */
  get isStackOrFixedDisplay(): boolean {
    return scannerTypeTakesMicroPlates(this.displayType);
  }

  /** Detected microloading add-on plates, shown as a distinct group under the primary Stack/Fixed
   *  list. Returns an empty array for other types (and when no micro plates were detected). The
   *  primary stack/fixed items keep rendering in the flat table. (#3523) */
  get detectedMicroPlateRows(): ResultItem[] {
    if (!this.isStackOrFixedDisplay) return [];
    return this.resultItems.filter(i => i.source.kind === 'plate');
  }

  /** The primary detected list for Stack/Fixed — the stack increments / dumbbell weights, excluding
   *  micro plates (which render in their own group via detectedMicroPlateRows). For other types this
   *  returns all resultItems unchanged. (#3523) */
  get detectedPrimaryRows(): ResultItem[] {
    if (!this.isStackOrFixedDisplay) return this.resultItems;
    return this.resultItems.filter(i => i.source.kind !== 'plate');
  }

  // ── Fake progress curve ────────────────────────────────────────────

  // per-tick sync from the shared fake-progress driver
  private onProgressTick() {
    if (this.viewDestroyed) {
      this.fakeProgress.stop();
      return;
    }
    this.cd.markForCheck();
  }

  // ── Scan-failure alerts (shared Try Again model) ───────────────────

  private async showErrorAlertAsync(message: TranslatedString) {
    await this.alertCtrl.showAlertAsync({
      title: LL.equipmentScanner.errorTitle(),
      message,
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
  }

  // like showErrorAlertAsync, but for transient failures it adds the shared
  // "Try Again" button so the user can re-run the scan in place. Returns 'retry'
  // when tapped, null otherwise.
  private async showScanErrorAsync(message: TranslatedString, opts: { resetCamera?: boolean; offerRetry?: boolean } = {}): Promise<'retry' | null> {
    // leave the 'analyzing' phase so the fake-progress overlay doesn't stay stuck
    // behind the alert — the alert shows over the collage, and Cancel / reset keeps
    // the user on the camera phase (a retry re-enters 'analyzing' via analyzeAsync).
    this.phase = 'camera';
    this.cd.markForCheck();
    // without a retry button this is just the plain OK-only alert
    if (!opts.offerRetry) {
      await this.showErrorAlertAsync(message);
      if (this.viewDestroyed) return null;
      if (opts.resetCamera) this.retakeAllPhotos();
      return null;
    }
    const choice = await showScanRetryAlertAsync(this.alertCtrl, LL.equipmentScanner.errorTitle(), message);
    if (this.viewDestroyed) return null;
    if (opts.resetCamera) this.retakeAllPhotos();
    return choice === 'retry' ? 'retry' : null;
  }

  // resolve the equipment-specific message for a failed scan: network copy when
  // offline, incomplete copy for a truncated/timed-out response, and
  // translateScanError (unsupported / bad-image / generic) otherwise.
  private scanErrorMessage(error: string | undefined, isOnline: boolean): TranslatedString {
    if (!isOnline) return LL.equipmentScanner.errorNetwork();
    if (error === ScanEquipmentError.IncompleteResponse || error === ScanEquipmentError.ProviderTimeout) {
      return LL.equipmentScanner.errorIncomplete();
    }
    return this.translateScanError(error ?? '');
  }

  // central handler for a failed equipment scan: consult the shared recovery
  // policy, show the right alert (Try Again for transient provider/network
  // failures) with the caller's message, and re-run the scan when tapped. The
  // caller must have already stopped the progress bar.
  private async handleScanFailureAsync(error: string | undefined, message: TranslatedString) {
    await runScanFailureRecoveryAsync({
      error,
      isOnline: this.onlineState.isLikelyOnline(),
      // showScanErrorAsync drops the analyzing phase so the alert shows over the collage
      showScanErrorAsync: alertOptions => this.showScanErrorAsync(message, alertOptions),
      scheduleOnNextTask: run => this.safeTimeout(run, 0),
      retryScan: () => { if (!this.viewDestroyed) void this.analyzeAsync(); },
    });
  }

  // ── Lifecycle ──────────────────────────────────────────────────────

  async ionViewCanLeave() {
    await this.cameraView?.stopCamera();
    return true;
  }

  async ionViewWillLeave() {
    this.fakeProgress.stop();
  }

  ngOnDestroy() {
    void this.cameraView?.stopCamera();
    this.fakeProgress.stop();
  }
}

/** The dev-only fake scan payload for one equipment type, used by the Mock button.
 *
 *  A function with no default arm rather than a switch assigning into a variable: `noImplicitReturns`
 *  then makes a type added without a fixture a compile error, where the old `case Fixed: default:`
 *  arm quietly handed every unhandled type the dumbbell fixture. */
function mockScanFixture(type: ScanSupportedEquipmentType): { suggestedName: string; items: EquipmentScanItem[] } {
  switch (type) {
    case EquipmentType.Stack:
      return {
        suggestedName: 'Cybex VR1 Lat Pulldown',
        items: [
          ...Array.from({ length: 10 }, (_, i) => ({
            kind: 'stack' as const,
            weight: (i + 1) * 5,
            confidence: i === 5 ? 0.55 : 0.92, // one interpolated value
          })),
          // two microloading add-on plates so the MOCK exercises the micro-plate review group
          { kind: 'plate' as const, weight: 0.625, confidence: 0.9 },
          { kind: 'plate' as const, weight: 0.625, confidence: 0.9 },
        ],
      };
    case EquipmentType.Plates:
      return {
        suggestedName: 'Eleiko IWF Training Plates',
        items: [20, 20, 15, 15, 10, 10, 5, 5, 2.5, 2.5].map(w => ({ kind: 'plate' as const, weight: w, confidence: 0.94 })),
      };
    case EquipmentType.Bar:
      return {
        suggestedName: 'Eleiko Sport Training 20kg',
        items: [
          { kind: 'bar' as const, weight: 20, confidence: 0.96 },
          { kind: 'collar' as const, weight: 2.5, confidence: 0.85 },
          { kind: 'plate' as const, weight: 20, confidence: 0.95 },
          { kind: 'plate' as const, weight: 20, confidence: 0.95 },
          { kind: 'plate' as const, weight: 10, confidence: 0.90 },
          { kind: 'plate' as const, weight: 5,  confidence: 0.65 },
        ],
      };
    case EquipmentType.Fixed:
      return {
        suggestedName: 'Powerblock Pro 50',
        items: [
          ...[5, 10, 15, 20, 25, 30, 35, 40, 45, 50].map(w => ({ kind: 'fixed' as const, weight: w, confidence: 0.91 })),
          // two microloading add-on plates so the MOCK exercises the micro-plate review group
          { kind: 'plate' as const, weight: 1.25, confidence: 0.9 },
          { kind: 'plate' as const, weight: 1.25, confidence: 0.9 },
        ],
      };
  }
}
